/**
 * Telegram Link Modal
 *
 * Shows options for connecting a Telegram account to receive notifications:
 * - Option 1: Deep link button (opens Telegram directly with pre-filled token)
 * - Option 2: Manual code with copy button (for when deep link isn't supported)
 *
 * The modal auto-generates a link code when opened, polls for successful
 * linking, and auto-closes with a success notification when linked.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Code,
  CopyButton,
  Divider,
  Loader,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBrandTelegram,
  IconCopy,
  IconCheck,
  IconExternalLink,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  useGenerateTelegramCode,
  useTelegramStatus,
} from '@/hooks/useNotificationPreferences';
import type { TelegramLinkResponse } from '@/types/notifications';

interface TelegramLinkModalProps {
  opened: boolean;
  onClose: () => void;
  onLinked?: () => void;
}

export function TelegramLinkModal({
  opened,
  onClose,
  onLinked,
}: TelegramLinkModalProps) {
  const [linkData, setLinkData] = useState<TelegramLinkResponse | null>(null);
  const {
    mutateAsync: generateCode,
    isPending,
    isError,
  } = useGenerateTelegramCode();

  // Poll for status only while modal is open and code has been generated
  const shouldPoll = opened && linkData !== null;
  const { data: status } = useTelegramStatus(shouldPoll, 3000);

  // Track whether we already handled the linked event (prevent double-fire)
  const hasHandledLink = useRef(false);

  // Generate code when modal opens
  useEffect(() => {
    if (opened && !linkData) {
      hasHandledLink.current = false;
      generateCode()
        .then(setLinkData)
        .catch(() => {
          // Error state handled by isError
        });
    }
  }, [opened, linkData, generateCode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setLinkData(null);
      hasHandledLink.current = false;
    }
  }, [opened]);

  // Auto-close on successful link
  useEffect(() => {
    if (status?.linked && !hasHandledLink.current) {
      hasHandledLink.current = true;
      notifications.show({
        title: 'Telegram connected',
        message: 'You will now receive task reminders via Telegram.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      onLinked?.();
      onClose();
    }
  }, [status?.linked, onClose, onLinked]);

  const handleOpenTelegram = useCallback(() => {
    // Only open Telegram URLs - guards against a javascript: payload if the
    // backend value were ever attacker-influenced
    if (linkData?.deep_link && /^(https:\/\/t\.me\/|tg:\/\/)/.test(linkData.deep_link)) {
      window.open(linkData.deep_link, '_blank', 'noopener,noreferrer');
    }
  }, [linkData?.deep_link]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Connect Telegram"
      size="sm"
    >
      <Stack gap="md">
        {isPending && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text>Generating link code...</Text>
          </Group>
        )}

        {isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to generate link code. Please try again.
          </Alert>
        )}

        {linkData && (
          <>
            {/* Option 1: Deep link */}
            <div>
              <Text fw={500} size="sm" mb="xs">
                Option 1: Quick link
              </Text>
              <Button
                fullWidth
                leftSection={<IconBrandTelegram size={18} />}
                rightSection={<IconExternalLink size={14} />}
                onClick={handleOpenTelegram}
              >
                Open Telegram
              </Button>
              <Text size="xs" c="dimmed" mt={4}>
                Opens the Reef Data Hub Alerts bot and connects automatically
              </Text>
            </div>

            <Divider label="or" labelPosition="center" />

            {/* Option 2: Manual code */}
            <div>
              <Text fw={500} size="sm" mb="xs">
                Option 2: Manual code
              </Text>
              <Text size="sm" c="dimmed" mb="xs">
                Send this code to @{linkData.bot_username} on Telegram:
              </Text>
              <Group gap="xs">
                <Code
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '1.1rem',
                    textAlign: 'center',
                  }}
                >
                  {linkData.link_code}
                </Code>
                <CopyButton value={linkData.link_code}>
                  {({ copied, copy }) => (
                    <Button
                      variant="outline"
                      onClick={copy}
                      leftSection={
                        copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )
                      }
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed" mt={4}>
                Code expires in 10 minutes
              </Text>
            </div>
          </>
        )}
      </Stack>
    </Modal>
  );
}
